
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';

type RouteGuardProps = {
  children: ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
};

export const RouteGuard = ({ 
  children, 
  requireAuth = true, 
  requireAdmin = false 
}: RouteGuardProps) => {
  const { 
    isAuthenticated, 
    isAdmin, 
    isAuthReady, 
    isProfileLoading, 
    userProfile, 
    profileStatus,
    refreshProfile,
    user 
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [retryCount, setRetryCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Wait for auth to be fully ready before making access decisions
    if (!isAuthReady) return;

    // Debug logging for auth checks
    if (process.env.NODE_ENV === 'development') {
      console.log('[RouteGuard] Auth check:', {
        requireAuth,
        requireAdmin,
        isAuthenticated,
        isAdmin,
        profileStatus,
        userRole: userProfile?.role,
        isAuthReady,
        pathname: location.pathname
      });
    }

    if (requireAuth && !isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in to access this page",
        variant: "destructive"
      });
      navigate('/auth/login', { state: { from: location.pathname } });
      return;
    }

    // Handle profile completion status routing for authenticated users
    if (isAuthenticated && profileStatus) {
      const currentPath = location.pathname;
      
      // Allow users to stay on their status-specific pages
      const statusPages = ['/auth/complete-profile', '/auth/pending-review', '/auth/profile-rejected'];
      
      switch (profileStatus) {
        case 'incomplete':
          if (!statusPages.includes(currentPath)) {
            console.log("📝 Redirecting to complete profile - profile incomplete");
            navigate('/auth/complete-profile', { replace: true });
            return;
          }
          break;
          
        case 'pending_review':
          if (currentPath !== '/auth/pending-review') {
            console.log("⏳ Redirecting to pending review - profile under review");
            navigate('/auth/pending-review', { replace: true });
            return;
          }
          break;
          
        case 'rejected':
          if (currentPath !== '/auth/profile-rejected') {
            console.log("❌ Redirecting to profile rejected - profile was rejected");
            navigate('/auth/profile-rejected', { replace: true });
            return;
          }
          break;
          
        case 'approved':
          // Only approved users can access the main application
          // Redirect away from status pages to marketplace if they somehow end up there
          if (statusPages.includes(currentPath)) {
            console.log("✅ Redirecting approved user away from status page");
            navigate('/marketplace', { replace: true });
            return;
          }
          
          // Normal admin check for approved users
          if (requireAdmin && !isAdmin) {
            toast({
              title: "Access denied",
              description: "You don't have permission to access this area",
              variant: "destructive"
            });
            navigate('/marketplace', { state: { from: location.pathname } });
            return;
          }
          break;
      }
    }
  }, [isAuthenticated, isAdmin, isAuthReady, profileStatus, requireAuth, requireAdmin, navigate, location, toast, userProfile?.role]);

  // Handle manual profile refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    const success = await refreshProfile();
    setIsRefreshing(false);
    
    if (success) {
      setRetryCount(0);
      toast({
        title: "Profile refreshed",
        description: "Your profile data has been updated",
      });
    } else {
      setRetryCount(prev => prev + 1);
      toast({
        title: "Refresh failed",
        description: "Unable to refresh profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading state while auth is not ready
  if (!isAuthReady) {
    const showRetryOption = isAuthenticated && user && !userProfile && retryCount > 0 && retryCount < 3;
    
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="h-8 w-8 bg-primary/20 rounded-full animate-spin border-2 border-primary/30 border-t-primary"></div>
            <p className="text-sm text-muted-foreground">
              {isProfileLoading ? 'Loading user profile...' : 'Checking authentication...'}
            </p>
          </div>
          
          {showRetryOption && (
            <div className="flex flex-col items-center gap-2 p-4 border border-orange-200 rounded-lg bg-orange-50">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Profile loading issue</span>
              </div>
              <p className="text-xs text-orange-600 text-center max-w-xs">
                Having trouble loading your profile. This might affect admin access.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="mt-2"
              >
                {isRefreshing ? (
                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Retry Profile Load
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
};
